import React, { useEffect, useState } from 'react';
import {
	LaboratoryRequestCreateDocumentInfo,
	LaboratoryRequestCreatePatient,
	PRIMARY_SAMPLE_TYPE,
} from './LaboratoryRequestCreateRequestHeaderForm';
import { LaboratoryRequestFormDataScheme } from '../LaboratoryRequestCreate/LaboratoryRequestCreateForm';
// @ts-ignore
import styles from './LaboratoryRequestCreateRecapitulation.module.scss';
import Text from '../../../../Components/Base/Text/Text';
import FormattedMomentDate, {
	DateFormat,
} from '../../../Base/Components/FormattedMomentDate/FormattedMomentDate';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import usePatientConsent from '../../../../Components/PatientProfile/Hooks/usePatientConsent';
import PatientNoConsentProvided from '../../../Patient/Components/PatientNoConsentProvided/PatientNoConsentProvided';
import useTranslation from '../../../../Hooks/useTranslation';
import ElectronicSignatureFields from '../../../Base/Components/ElectronicSignatureFields/ElectronicSignatureFields';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import useElectronicSignature from '../../../Certificates/Hooks/useElectronicSignature';
import ElectronicSignaturePasswordRequestModal from '../../../Certificates/Components/ElectronicSignaturePasswordRequest/ElectronicSignaturePasswordRequestModal';

interface Props {
	onConfirm: (values: LaboratoryRequestCreateRecapitulationFormValues) => void;
	patientId: string;
	data: {
		patient?: LaboratoryRequestCreatePatient;
		documentInfo?: LaboratoryRequestCreateDocumentInfo;
		data?: LaboratoryRequestFormDataScheme[];
	};
}

export interface LaboratoryRequestCreateRecapitulationFormValues {
	saveToECard: boolean;
	electronicSignature: boolean;
	electronicSignatureTimestamp: boolean;
}

const LaboratoryRequestCreateRecapitulation: React.FC<Props> = ({
	onConfirm,
	data,
	patientId,
}) => {
	const patient = data?.patient;
	const documentInfo = data?.documentInfo;
	const requestData = data?.data;
	const { isConsentProvided } = usePatientConsent(patientId);
	const {
		isQualifiedCertificateForElectronicSignatureUsed,
		isPasswordRequiredForElectronicSignatureUsed,
		isElectronicSignatureTimestampAllowed,
	} = useElectronicSignature();
	const { t } = useTranslation();
	const [formValues, setFormValues] = useState<
		LaboratoryRequestCreateRecapitulationFormValues | undefined
	>();
	const [isPasswordRequestOpen, setIsPasswordRequestOpen] = useState(false);

	const formMethods = useForm({
		defaultValues: {
			saveToECard: false,
			electronicSignature: false,
			electronicSignatureTimestamp: false,
		},
	});

	//SET DEFAULT VALUES AFTER ALL DATA IS LOADED
	useEffect(() => {
		formMethods.reset({
			saveToECard: isConsentProvided,
			electronicSignature: isQualifiedCertificateForElectronicSignatureUsed,
			electronicSignatureTimestamp:
				isQualifiedCertificateForElectronicSignatureUsed &&
				isElectronicSignatureTimestampAllowed,
		});
	}, [
		isConsentProvided,
		isQualifiedCertificateForElectronicSignatureUsed,
		isElectronicSignatureTimestampAllowed,
	]);

	const handleSubmit = (
		values: LaboratoryRequestCreateRecapitulationFormValues,
	) => {
		if (
			isPasswordRequiredForElectronicSignatureUsed &&
			values.electronicSignature
		) {
			setFormValues(values);
			setIsPasswordRequestOpen(true);
			return;
		}
		onConfirm(values);
	};

	const handleOnVerify = () => {
		if (!formValues) return;
		onConfirm(formValues);
	};

	return (
		<>
			<div className={styles.wrapper}>
				<div className='row'>
					<div className='col-6'>
						{requestData && (
							<div className={styles.group}>
								<div className={styles.item}>
									<Text
										className={styles.title}
										tx='component.laboratoryRequestCreateRecapitulation.requests'
										colon
										firstUpper
									/>
								</div>
								{requestData?.map(item => (
									<div className={styles.item}>
										{item.note} {item.value !== 'true' && `: ${item.value}`}
									</div>
								))}
							</div>
						)}
					</div>
					<div className='col-6'>
						{patient && (
							<div className={styles.group}>
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.patient'
										className={styles.label}
										colon
										firstUpper
									/>
									<div>
										{patient.firstName} {patient.lastName}
									</div>
								</div>
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.insuranceNumber'
										className={styles.label}
										colon
										firstUpper
									/>
									<div>{patient.insuranceNumber}</div>
								</div>
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.birthDate'
										className={styles.label}
										colon
										firstUpper
									/>
									<FormattedMomentDate
										date={patient.birthDate}
										format={DateFormat.DATE}
									/>
								</div>
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.address'
										className={styles.label}
										colon
										firstUpper
									/>
									<div>
										{patient.address.street} {patient.address.descriptiveNumber}{' '}
										{patient.address.guideNumber
											? `/${patient.address.guideNumber}`
											: ''}
										{', '}
										{patient.address.city} {patient.address.postCode}
									</div>
								</div>
								{patient.insuranceCompany && (
									<div className={styles.item}>
										<Text
											tx='component.laboratoryRequestCreateRecapitulation.insuranceCompany'
											className={styles.label}
											colon
											firstUpper
										/>
										{patient.insuranceCompany.label}
									</div>
								)}
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.gender'
										className={styles.label}
										colon
										firstUpper
									/>
									{patient.gender.label}
								</div>
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.diagnosis'
										className={styles.label}
										colon
										firstUpper
									/>
									{patient.diagnosis.label}
								</div>
							</div>
						)}
						{documentInfo && (
							<div className={styles.group}>
								{documentInfo.tookBy?.label && (
									<div className={styles.item}>
										<Text
											tx='component.laboratoryRequestCreateRecapitulation.tookBy'
											className={styles.label}
											colon
											firstUpper
										/>
										<div>{documentInfo.tookBy.label}</div>
									</div>
								)}
								{documentInfo.tookByCustom && (
									<div className={styles.item}>
										<Text
											tx='component.laboratoryRequestCreateRecapitulation.tookBy'
											className={styles.label}
											colon
											firstUpper
										/>
										<div>{documentInfo.tookByCustom}</div>
									</div>
								)}
								{documentInfo.date && (
									<div className={styles.item}>
										<Text
											tx='component.laboratoryRequestCreateRecapitulation.date'
											className={styles.label}
											colon
											firstUpper
										/>
										<FormattedMomentDate
											date={documentInfo.date}
											format={DateFormat.DATE}
										/>
									</div>
								)}
								{documentInfo.time && (
									<div className={styles.item}>
										<Text
											tx='component.laboratoryRequestCreateRecapitulation.time'
											className={styles.label}
											colon
											firstUpper
										/>
										<FormattedMomentDate
											date={documentInfo.time}
											format={DateFormat.TIME}
										/>
									</div>
								)}
								{documentInfo.primarySampleType && (
									<div className={styles.item}>
										<Text
											tx='component.laboratoryRequestCreateRecapitulation.primarySampleType'
											className={styles.label}
											colon
											firstUpper
										/>
										{t(
											PRIMARY_SAMPLE_TYPE.find(
												item => item.value === documentInfo.primarySampleType,
											)?.title,
										)}
									</div>
								)}
								<div className={styles.item}>
									<Text
										tx='component.laboratoryRequestCreateRecapitulation.statim'
										className={styles.label}
										colon
										firstUpper
									/>
									<Text
										tx={documentInfo.statim ? 'base.yes' : 'base.no'}
										inline
										firstUpper
									/>
								</div>
								{documentInfo.statimPhoneNumber && (
									<div className={styles.item}>
										<Text
											tx='component.laboratoryRequestCreateRecapitulation.statimPhoneNumber'
											className={styles.label}
											colon
											firstUpper
										/>
										{documentInfo.statimPhoneNumber}
									</div>
								)}
							</div>
						)}
					</div>
				</div>
				<FormWrapper formMethods={formMethods}>
					<div>
						{isConsentProvided ? (
							<CheckboxField
								tx='component.laboratoryRequestCreateRecapitulation.saveToECard'
								name='saveToECard'
								isWrapped
								isRequired
							/>
						) : (
							<PatientNoConsentProvided patientId={patientId} />
						)}
					</div>
					<ElectronicSignatureFields />
					<div className='mt-3 d-flex'>
						<SubmitButton onSubmit={handleSubmit} title='base.create' />
					</div>
				</FormWrapper>
			</div>
			{isPasswordRequestOpen && (
				<ElectronicSignaturePasswordRequestModal
					isOpen
					onClose={() => setIsPasswordRequestOpen(false)}
					onVerify={handleOnVerify}
					submitButtonText='base.sign'
				/>
			)}
		</>
	);
};

export default LaboratoryRequestCreateRecapitulation;
